import { ReactNode, useEffect, useMemo } from 'react';

import { GetChecksResponseDTO, GetUnitsResponseDTO, MODULE } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { ColumnFiltersState, RowSelectionState } from '@tanstack/react-table';

import manholeShrugVector from '@/assets/vectors/manhole-shrug.svg';
import UndoIcon from '@/components/icons/undo';
import { TablePageTable } from '@/components/table-page/table-page-table';
import { useModulesStore } from '@/stores/modules.store';

import { createAssetScheduleCheckColumns } from './table-meta';

export function AssetScheduleChecksTable({
  notFoundDisplay,
  data,
  loading,
  units,
  onRowSelectionChange,
  rowSelection,
  columnFilters,
  onColumnFiltersChange,
  sorting: sortingProp,
  grouping,
  columnOrder,
  columnVisibility,
  onSortingChange,
  onRowClick,
}: {
  notFoundDisplay: ReactNode;
  data: GetChecksResponseDTO['data'];
  loading: boolean;
  units: GetUnitsResponseDTO['data'];
  onRowSelectionChange: (selectedRows: RowSelectionState) => void;
  rowSelection: RowSelectionState;
  columnFilters: ColumnFiltersState;
  onColumnFiltersChange: (columnFilters: ColumnFiltersState) => void;
  sorting: { key?: string; direction?: 'asc' | 'desc' };
  grouping: string[];
  columnOrder: Array<{ id: string; active: boolean }>;
  columnVisibility: Record<string, boolean>;
  onSortingChange: (sorting: { key?: string; direction?: 'asc' | 'desc' }) => void;
  onRowClick?: (row: GetChecksResponseDTO['data'][number]) => void;
}) {
  const { syncModuleColumns } = useModulesStore();

  const sorting = useMemo(
    () => (sortingProp.key ? [{ id: sortingProp.key, desc: sortingProp.direction === 'desc' }] : []),
    [sortingProp.key, sortingProp.direction],
  );

  useEffect(() => {
    syncModuleColumns(MODULE.AssetScheduleChecks, createAssetScheduleCheckColumns());
  }, [syncModuleColumns]);

  return (
    <div className="flex-1 bg-white">
      <TablePageTable<GetChecksResponseDTO['data'][number]>
        data={data}
        columns={createAssetScheduleCheckColumns({ units })}
        notFoundDisplay={notFoundDisplay}
        loading={loading}
        onSortingChange={onSortingChange}
        sorting={sorting}
        grouping={grouping}
        columnOrder={(columnOrder || []).map((column) => column.id)}
        rowSelection={rowSelection}
        onRowSelectionChange={onRowSelectionChange}
        columnVisibility={columnVisibility}
        columnFilters={columnFilters}
        onColumnFiltersChange={onColumnFiltersChange}
        onRowClick={onRowClick}
      />
    </div>
  );
}

export function AssetChecksNoResultsDisplay() {
  return (
    <div className="absolute inset-0 flex h-full w-full flex-col items-center justify-center gap-4 p-4">
      <img src={manholeShrugVector} alt="Manhole shrug" className="h-28" />
      <span className="text-sm">No checks match your search</span>
    </div>
  );
}

export function AssetChecksNoFiltersMatchDisplay({ removeColumnFilters }: { removeColumnFilters: () => void }) {
  return (
    <div className="bg-grey-1 absolute inset-0 flex h-full w-full flex-col items-center justify-center gap-4 p-4">
      <img src={manholeShrugVector} alt="Manhole shrug" className="h-28" />
      <span className="text-xl font-medium">No checks match your filters</span>
      <span className="text-secondary-text text-sm">Try adjusting your column filters</span>
      <div className="pointer-events-auto flex gap-2">
        <Button variant="grey" onClick={removeColumnFilters} className="gap-1 font-normal">
          <UndoIcon />
          Remove all filters
        </Button>
      </div>
    </div>
  );
}

export function AssetChecksEmptyDisplay() {
  return (
    <div className="absolute inset-0 m-auto flex w-[383px] flex-col items-center justify-center gap-[20px]">
      <img src={manholeShrugVector} alt="Manhole shrug" className="h-28" />
      <span className="text-xl leading-[30px] font-medium">No schedule checks</span>
      <div className="flex flex-col gap-0 text-center">
        <span className="text-secondary-text text-sm">Schedule checks will appear here when schedules are deployed to this asset.</span>
      </div>
    </div>
  );
}
